import React from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import PageMeta from "@/components/seo/PageMeta";
import { FAQSchema } from "@/components/seo/schemas";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CheckCircle2, ArrowRight, Calendar, Mail, Shield, Eye, Settings, FileCheck } from "lucide-react";
import { BOOKING_URL, CONTACT_EMAIL } from "@/config/constants";

const HOMEPAGE_FAQS = [
  {
    question: "Is this legal advice or an audit opinion?",
    answer: "No. BoardSight provides governance education and advisory support—not legal advice or audit opinions. Involve counsel for jurisdiction-specific interpretation.",
  },
  {
    question: "Do you need access to systems or proprietary data?",
    answer: "No. We work from policy documents, risk registers, and committee-level information. No system access required.",
  },
  {
    question: "Can management attend?",
    answer: "Yes. Many committees include key management (GC, CRO, CISO, Chief Audit Executive) to align on oversight expectations and action items.",
  },
  {
    question: "Is this only for public companies?",
    answer: "No. Any organization with a board or oversight body responsible for enterprise risk can benefit—public, private, non-profit, or government.",
  },
  {
    question: "How quickly can this be scheduled?",
    answer: "Most briefings can be scheduled within 2–3 weeks. We accommodate committee calendars and can work around board meeting schedules.",
  },
];

const OVERSIGHT_EXPECTATIONS = [
  { icon: Eye, label: "Visible", desc: "Inventory and ownership" },
  { icon: Settings, label: "Governed", desc: "Decision rights and escalation" },
  { icon: Shield, label: "Controlled", desc: "Testing, monitoring, change management" },
  { icon: FileCheck, label: "Assured", desc: "Internal audit coverage and reporting" },
];

const FRAMEWORKS = [
  "NIST AI RMF",
  "ISO 42001",
  "EU AI Act",
  "OSFI",
  "APRA",
];

const Index: React.FC = () => {
  return (
    <Layout>
      <PageMeta
        title="BoardSight | Defensible AI Oversight for Audit & Risk Committees"
        description="BoardSight equips Audit and Risk Committees with chair-ready checklists, board-ready slide decks, and 30/60/90-day action plans for defensible AI oversight."
        canonicalUrl="https://boardsight.ai"
      />
      <FAQSchema faqs={HOMEPAGE_FAQS} />

      {/* Hero Section */}
      <section className="pt-16 pb-20 md:pt-24 md:pb-28 bg-gradient-to-b from-muted/50 to-background">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6">
              Defensible AI Oversight for Audit &amp; Risk Committees
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-4 max-w-3xl mx-auto">
              Audit and Risk Committees are being asked to oversee AI like any other enterprise risk: with clear accountability, controls, reporting, and assurance.
            </p>
            <p className="text-lg text-muted-foreground mb-8 max-w-3xl mx-auto">
              This briefing equips your committee with a chair-ready checklist, a board-ready slide deck, and a 30/60/90-day action plan for management—so oversight becomes practical, not theoretical.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Book a Chair Call
                </a>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground">
                <Link to="/quiz">
                  Take the 5-Minute Assessment
                </Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Built for Audit Committee Chairs, Risk Committee Chairs, and Directors overseeing ERM, compliance, cyber/privacy, third‑party risk, and internal audit.
            </p>
          </div>
        </div>
      </section>

      {/* AI has moved from strategy to oversight */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
              AI has moved from strategy to oversight.
            </h2>
            <p className="text-lg text-muted-foreground mb-10">
              AI is already influencing core processes—financial decisions, operational workflows, customer interactions, vendor tools, and employee productivity. As expectations rise, Audit and Risk Committees are increasingly expected to ensure AI risk is:
            </p>
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6">
              {OVERSIGHT_EXPECTATIONS.map((item) => (
                <Card key={item.label} className="bg-card border-border text-center">
                  <CardContent className="pt-6">
                    <item.icon className="h-8 w-8 text-primary mx-auto mb-3" />
                    <p className="font-bold text-foreground text-lg">{item.label}</p>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Frameworks trust bar */}
      <section className="py-10 border-y border-border">
        <div className="container-custom">
          <p className="text-center text-sm text-muted-foreground mb-4">Aligned with leading governance frameworks</p>
          <div className="flex flex-wrap justify-center gap-4 md:gap-8">
            {FRAMEWORKS.map((fw) => (
              <span key={fw} className="text-sm font-semibold text-foreground/70 bg-muted/50 px-4 py-2 rounded-full">
                {fw}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* What We Offer — Board Briefings, Scenario Activations, Courses */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Choose the level of support your committee needs.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-card border-border hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-foreground">
                  Board Briefings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  Committee-ready AI oversight briefings with a slide deck, chair checklist, and 30/60/90-day action plan—not generic AI training.
                </p>
                <Button asChild variant="link" className="p-0 text-primary hover:text-primary/80">
                  <Link to="/board-briefings" className="inline-flex items-center gap-2">
                    Learn More <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-card border-border hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-foreground">
                  Scenario Activations
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  Facilitated tabletop exercises simulating real-world AI incidents to test your committee's escalation, communication, and decision-making.
                </p>
                <Button asChild variant="link" className="p-0 text-primary hover:text-primary/80">
                  <Link to="/scenario-activations" className="inline-flex items-center gap-2">
                    Learn More <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-card border-border hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-foreground">
                  Courses
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  Structured programs for directors—practical oversight frameworks, board-ready templates, and the questions committees should be asking.
                </p>
                <Button asChild variant="link" className="p-0 text-primary hover:text-primary/80">
                  <Link to="/courses" className="inline-flex items-center gap-2">
                    View Courses <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Assessment CTA */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Start with a 5‑minute diagnostic.
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Benchmark your committee's AI oversight readiness and identify the highest‑impact gaps to address next.
            </p>
            <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground">
              <Link to="/quiz">
                Begin the Assessment
              </Link>
            </Button>
            <p className="text-sm text-muted-foreground mt-4">
              Your data is used only to generate your results.
            </p>
          </div>
        </div>
      </section>

      {/* If any of these are happening */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-8 text-center">
              If any of these are happening, this briefing is for you.
            </h2>
            <ul className="space-y-4">
              {[
                "We don't know where AI is being used across the business.",
                "We need a clear AI governance owner and escalation path.",
                "Internal Audit is being asked to audit AI, but scope and criteria are unclear.",
                "We're buying AI-enabled vendor tools and need defensible third‑party oversight.",
                "Cyber/privacy leaders are concerned about AI data leakage and new attack paths.",
                "The committee needs a reporting cadence that's meaningful—not noise.",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-lg text-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-8 text-center">
              Frequently Asked Questions
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {HOMEPAGE_FAQS.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left text-foreground hover:text-primary">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Make AI oversight practical—before it becomes urgent.
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              If your Audit or Risk Committee is being asked where the AI oversight model is, this briefing gives you a defensible answer and a clear path forward.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Book a Chair Call
                </a>
              </Button>
              <Button asChild variant="outline" size="lg">
                <a href={`mailto:${CONTACT_EMAIL}`} className="inline-flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  {CONTACT_EMAIL}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
